import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../../services/api';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

const C = {
  purple: '#552583', gold: '#FDB927',
  bg: '#E4F0F6', white: '#ffffff',
  text: '#1c1917', textMuted: '#78716c',
  border: 'rgba(28,25,23,0.08)', borderMed: 'rgba(28,25,23,0.12)',
  green: '#1A9E64', red: '#D03530',
};

const BRAND_COLOR = {
  visa: '#1A1F71', mastercard: '#EB001B',
  amex: '#007BC1', discover: '#FF6600',
  cartes_bancaires: '#552583', unknown: '#8A7AA8',
};

const BRAND_LABEL = {
  visa: 'Visa', mastercard: 'Mastercard', amex: 'American Express',
  discover: 'Discover', cartes_bancaires: 'CB', unknown: 'Carte',
};

// ── Saved card item ───────────────────────────────────────────────────────────
function CardItem({ method, onDelete, deleting }) {
  const color = BRAND_COLOR[method.brand] || BRAND_COLOR.unknown;
  const label = BRAND_LABEL[method.brand] || method.brand;
  const exp = `${String(method.exp_month).padStart(2, '0')}/${String(method.exp_year).slice(-2)}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', borderBottom: `0.5px solid ${C.border}` }}>
      {/* Card art */}
      <div style={{ width: 56, height: 36, borderRadius: 6, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: '0.6rem', fontWeight: 800, color: '#fff', letterSpacing: '0.04em', textTransform: 'uppercase' }}>{label}</span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: C.text }}>•••• •••• •••• {method.last4}</div>
        <div style={{ fontSize: '0.72rem', color: C.textMuted, marginTop: 2 }}>Expire {exp}</div>
      </div>
      <button
        onClick={() => onDelete(method.id)}
        disabled={deleting === method.id}
        style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: `1px solid rgba(208,53,48,0.2)`, background: 'transparent', color: C.red, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', opacity: deleting === method.id ? 0.5 : 1, fontFamily: 'inherit' }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
        {deleting === method.id ? 'Suppression...' : 'Supprimer'}
      </button>
    </div>
  );
}

// ── Add card form (inside Stripe Elements) ────────────────────────────────────
function AddCardForm({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');

    const { error: submitErr } = await elements.submit();
    if (submitErr) { setError(submitErr.message); setLoading(false); return; }

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.origin + '/profile/payment-methods' },
      redirect: 'if_required',
    });

    if (result.error) {
      setError(result.error.message);
    } else {
      onSuccess();
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ padding: '20px 24px' }}>
      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgba(208,53,48,0.08)', border: '1px solid rgba(208,53,48,0.15)', color: C.red, fontSize: '0.78rem', marginBottom: 16 }}>
          {error}
        </div>
      )}
      <div style={{ borderRadius: 10, border: `1px solid ${C.borderMed}`, overflow: 'hidden', marginBottom: 16 }}>
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button type="submit" disabled={!stripe || loading}
          style={{ flex: 1, padding: '12px 20px', borderRadius: 10, background: C.purple, color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', opacity: loading ? 0.7 : 1, fontFamily: 'inherit' }}>
          {loading ? 'Enregistrement...' : 'Enregistrer la carte'}
        </button>
        <button type="button" onClick={onCancel}
          style={{ padding: '12px 20px', borderRadius: 10, background: 'transparent', color: C.textMuted, border: `1px solid ${C.borderMed}`, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' }}>
          Annuler
        </button>
      </div>
      <p style={{ fontSize: '0.68rem', color: C.textMuted, textAlign: 'center', marginTop: 12 }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ marginRight: 4, verticalAlign: 'middle' }}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        Sécurisé par Stripe — vos données ne transitent pas par nos serveurs
      </p>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchMethods = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/payments/methods');
      setMethods(res.data.methods || []);
    } catch {
      setError('Impossible de charger vos cartes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMethods(); }, [fetchMethods]);

  const handleShowAdd = async () => {
    setError('');
    try {
      const res = await api.post('/payments/setup-intent');
      setClientSecret(res.data.clientSecret);
      setShowAdd(true);
    } catch {
      setError('Impossible d\'initialiser l\'ajout de carte.');
    }
  };

  const handleCardAdded = async () => {
    setShowAdd(false);
    setClientSecret('');
    setSuccess('Carte enregistrée avec succès.');
    await fetchMethods();
    setTimeout(() => setSuccess(''), 4000);
  };

  const handleDelete = async (pmId) => {
    setDeleting(pmId);
    try {
      await api.delete(`/payments/methods/${pmId}`);
      setMethods(m => m.filter(x => x.id !== pmId));
      setSuccess('Carte supprimée.');
      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Erreur lors de la suppression.');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, padding: '40px 16px' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>

        {/* Back */}
        <Link to="/profile" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: C.textMuted, textDecoration: 'none', marginBottom: 28, fontWeight: 600 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
          Mon profil
        </Link>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.02em', color: C.text, margin: 0 }}>Moyens de paiement</h1>
          <p style={{ fontSize: '0.82rem', color: C.textMuted, marginTop: 6 }}>Gérez vos cartes enregistrées pour payer plus vite.</p>
        </div>

        {/* Alerts */}
        {error && (
          <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(208,53,48,0.08)', border: '1px solid rgba(208,53,48,0.15)', color: C.red, fontSize: '0.8rem', marginBottom: 16 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(26,158,100,0.08)', border: '1px solid rgba(26,158,100,0.2)', color: C.green, fontSize: '0.8rem', marginBottom: 16 }}>
            {success}
          </div>
        )}

        {/* Cards list */}
        <div style={{ background: C.white, borderRadius: 14, border: `0.5px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '16px 20px', borderBottom: `0.5px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: C.text }}>Cartes enregistrées</span>
            <span style={{ fontSize: '0.72rem', color: C.textMuted, fontWeight: 500 }}>{methods.length} carte{methods.length !== 1 ? 's' : ''}</span>
          </div>

          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', color: C.textMuted, fontSize: '0.82rem' }}>Chargement...</div>
          ) : methods.length === 0 && !showAdd ? (
            <div style={{ padding: '32px', textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(85,37,131,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.purple} strokeWidth="1.5" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
              </div>
              <p style={{ fontSize: '0.85rem', color: C.text, fontWeight: 600, margin: '0 0 4px' }}>Aucune carte enregistrée</p>
              <p style={{ fontSize: '0.75rem', color: C.textMuted, margin: 0 }}>Ajoutez une carte pour payer en un clic.</p>
            </div>
          ) : (
            methods.map(m => (
              <CardItem key={m.id} method={m} onDelete={handleDelete} deleting={deleting} />
            ))
          )}

          {/* Add card form */}
          {showAdd && clientSecret && (
            <Elements stripe={stripePromise} options={{
              clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: C.purple,
                  borderRadius: '8px',
                  fontFamily: 'Montserrat, Inter, sans-serif',
                },
              },
            }}>
              <AddCardForm onSuccess={handleCardAdded} onCancel={() => { setShowAdd(false); setClientSecret(''); }} />
            </Elements>
          )}
        </div>

        {/* Add button */}
        {!showAdd && (
          <button onClick={handleShowAdd}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '14px', borderRadius: 12, background: C.purple, color: '#fff', border: 'none', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'inherit', boxShadow: '0 4px 16px rgba(85,37,131,0.2)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Ajouter une carte
          </button>
        )}

        {/* Security note */}
        <div style={{ marginTop: 24, padding: '14px 16px', borderRadius: 10, background: 'rgba(85,37,131,0.04)', border: `0.5px solid rgba(85,37,131,0.1)`, display: 'flex', gap: 10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.purple} strokeWidth="1.8" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          <p style={{ fontSize: '0.75rem', color: C.textMuted, margin: 0, lineHeight: 1.6 }}>
            Vos données de carte sont gérées directement par <strong style={{ color: C.text }}>Stripe</strong> et ne sont jamais stockées sur nos serveurs. Usafiri est conforme PCI-DSS via Stripe.
          </p>
        </div>
      </div>
    </div>
  );
}
